import { Box, Typography } from '../../../components/ui';
import type { Article } from '../../../types/knowledgeBase';
import { ARTICLE_TYPE_LABELS } from '../../../types/knowledgeBase';
import { formatTimeAgo } from '../../../utils/dateUtils';
import './ArticleCard.css';

interface ArticleCardProps {
  article: Article;
  onClick: () => void;
}

function getContentSummary(article: Article): string {
  switch (article.articleContent.type) {
    case 'how-to': {
      const stepCount = article.articleContent.content.steps.length;
      return stepCount === 1 ? '1 step' : `${stepCount} steps`;
    }
    case 'known-error': {
      const systemCount = article.articleContent.content.affectedSystems.length;
      return systemCount === 1 ? '1 system' : `${systemCount} systems`;
    }
    case 'sop-runbook': {
      const stepCount = article.articleContent.content.procedureSteps.length;
      return stepCount === 1 ? '1 step' : `${stepCount} steps`;
    }
    default:
      return '';
  }
}

export function ArticleCard({ article, onClick }: ArticleCardProps) {
  const contentSummary = getContentSummary(article);

  return (
    <button
      className="article-card"
      onClick={onClick}
      type="button"
      aria-label={`View article: ${article.title}`}
    >
      <Box className="article-card__header">
        <span className="article-card__category">{article.category}</span>
        <span className={`article-card__type article-card__type--${article.articleType}`}>
          {ARTICLE_TYPE_LABELS[article.articleType]}
        </span>
      </Box>

      <Typography variant="subtitle1" component="h3" className="article-card__title">
        {article.title}
      </Typography>

      <Typography variant="body2" className="article-card__description">
        {article.description}
      </Typography>

      <Box className="article-card__tags" aria-label="Tags">
        {article.tags.slice(0, 3).map((tag) => (
          <span key={tag} className="article-card__tag">
            {tag}
          </span>
        ))}
        {article.tags.length > 3 && (
          <span className="article-card__tag article-card__tag--more">
            +{article.tags.length - 3}
          </span>
        )}
      </Box>

      <Box className="article-card__footer">
        <Box className="article-card__stats">
          <span className="article-card__stat">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M2 4h10M2 7h10M2 10h6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            {contentSummary}
          </span>
          <span className="article-card__stat">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
              <circle cx="7" cy="7" r="2" fill="currentColor" />
            </svg>
            {article.viewCount} views
          </span>
        </Box>
        <Typography variant="caption" className="article-card__date">
          Updated {formatTimeAgo(article.updatedAt)}
        </Typography>
      </Box>
    </button>
  );
}
